import type { SupabaseClient } from '@supabase/supabase-js'
import { Tables, TablesInsert, TablesUpdate } from '@/supabase/types'
import type { Database } from '@/supabase/types'

import { createServiceClient } from '@/lib/supabase/service-client'
import { isDev } from '@/lib/utils/isDev'

import { submitFeedback } from './omega/submit-feedback'
import { addEmbeddingsToMessage } from './chat-search'
import { getChatByIdForUser } from './chats'

type AuthenticatedFetch = typeof fetch

function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return require('@/lib/supabase/browser-client').supabase as SupabaseClient<Database>
}

const getAuthenticatedFetch = (): AuthenticatedFetch => {
  const mod = require('@/lib/auth/authenticated-fetch') as {
    authenticatedFetch: AuthenticatedFetch
  }

  return mod.authenticatedFetch
}

async function readJsonError(response: Response, fallbackMessage: string) {
  const responseText = await response.text().catch(() => '')

  if (!responseText) {
    return fallbackMessage
  }

  try {
    const payload = JSON.parse(responseText) as { error?: string }
    return payload.error || fallbackMessage
  } catch {
    return responseText
  }
}

async function readBrowserMessageById(messageId: string) {
  const response = await getAuthenticatedFetch()(`/api/messages/${encodeURIComponent(messageId)}`, {
    cache: 'no-store',
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Messages not found'))
  }

  return (await response.json()) as Tables<'messages'>
}

async function readBrowserMessagesByChatId(
  chatId: string,
  limit?: number,
  pagination?: {
    before?: string | null
    after?: string | null
  }
) {
  const searchParams = new URLSearchParams()

  if (typeof limit === 'number' && limit > 0) {
    searchParams.set('limit', String(limit))
  }

  if (pagination?.before) {
    searchParams.set('before', pagination.before)
  }

  if (pagination?.after) {
    searchParams.set('after', pagination.after)
  }

  const queryString = searchParams.toString()
  const response = await getAuthenticatedFetch()(
    `/api/chat/${encodeURIComponent(chatId)}/messages${queryString ? `?${queryString}` : ''}`,
    {
      cache: 'no-store',
      credentials: 'include'
    }
  )

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Messages not found'))
  }

  return (await response.json()) as Tables<'messages'>[]
}

async function createBrowserMessages(messages: TablesInsert<'messages'>[]) {
  const firstChatId = messages[0]?.chat_id?.trim()

  if (!firstChatId) {
    throw new Error('chat_id required')
  }

  if (messages.some((message) => message.chat_id !== firstChatId)) {
    throw new Error('Browser message batch must target a single chat')
  }

  const response = await getAuthenticatedFetch()(`/api/chat/${encodeURIComponent(firstChatId)}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ messages })
  })

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to create messages'))
  }

  return (await response.json()) as Tables<'messages'>[]
}

async function updateBrowserMessage(
  messageId: string,
  message: Partial<TablesUpdate<'messages'>>
) {
  const response = await getAuthenticatedFetch()(`/api/messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(message)
  })

  if (!response.ok) {
    throw new Error(await readJsonError(response, 'Failed to update message'))
  }

  return (await response.json()) as Tables<'messages'>
}

// Existing functions
export const getMessageById = async (messageId: string, client?: SupabaseClient<Database>) => {
  if (!client && typeof window !== 'undefined') {
    return readBrowserMessageById(messageId)
  }

  const c = getDefaultClient(client)
  const { data: message, error } = await c
    .from('messages')
    .select('*')
    .eq('id', messageId)
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return message
}

export const getMessageByIdForUser = async (
  messageId: string,
  userId: string,
  client?: SupabaseClient<Database>
) => {
  if (!messageId?.trim() || !userId?.trim()) {
    return null
  }

  const c = getDefaultClient(client)
  const message = await getMessageById(messageId, c)

  if (!message) {
    return null
  }

  const chat = await getChatByIdForUser(message.chat_id, userId, c)
  return chat ? message : null
}

export const getMessageCountForModel = async (
  userId: string,
  model: string,
  date?: Date,
  client?: SupabaseClient<Database>
) => {
  const c = getDefaultClient(client)
  if (!date) {
    date = new Date()
  }

  const { data, error } = await c
    .from('daily_message_count' as any)
    .select('count')
    .eq('user_id', userId)
    .eq('model_id', model)
    .eq('day', date.toISOString().split('T')[0])

  if (error) {
    isDev && console.error('Error fetching daily message count:', error)
    throw new Error('Could not fetch message count')
  }

  return data?.[0]?.count || 0
}

// --------------

/**
 * Count user messages for a specific model since a timestamp
 */
export const getMessageCountForModelSince = async (
  userId: string,
  model: string,
  since: Date,
  client?: SupabaseClient<Database>
) => {
  const c = getDefaultClient(client)
  const { count, error } = await c
    .from('messages')
    .select('*', { count: 'exact', head: true } as any)
    .eq('user_id', userId)
    .eq('role', 'user')
    .eq('model_id', model)
    .gt('created_at', since.toISOString())

  if (error) {
    isDev && console.error('Error fetching windowed message count:', error)
    throw new Error('Could not fetch windowed message count')
  }

  return count || 0
}

// --------------

/**
 * Find the oldest user message for a model inside a rolling window.
 */
export const getOldestMessageCreatedAtForModelSince = async (
  userId: string,
  model: string,
  since: Date,
  client?: SupabaseClient<Database>
) => {
  const c = getDefaultClient(client)
  const { data, error } = await c
    .from('messages')
    .select('created_at')
    .eq('user_id', userId)
    .eq('role', 'user')
    .eq('model_id', model)
    .gt('created_at', since.toISOString())
    .order('created_at', { ascending: true })
    .limit(1)

  if (error) {
    isDev && console.error('Error fetching oldest windowed message:', error)
    throw new Error('Could not fetch oldest windowed message')
  }

  const row = Array.isArray(data) ? data[0] : null
  return typeof row?.created_at === 'string' ? row.created_at : null
}

export const getMessageCount = async (since?: Date, client?: SupabaseClient<Database>) => {
  if (!since) {
    // one day ago
    since = new Date(new Date().getTime() - 24 * 60 * 60 * 1000)
  }

  const c = getDefaultClient(client)
  const { count, error } = await c
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .gt('created_at', since.toISOString())
    .eq('role', 'user')

  if (error) {
    throw new Error(error.message)
  }

  return count
}

/**
 * Get messages for a chat with optional pagination support
 *
 * @param chatId - The ID of the chat to get messages for
 * @param limit - Optional limit on number of messages to return (default: all messages)
 * @param pagination - Optional pagination parameters
 * @param pagination.before - Get messages created before this timestamp
 * @param pagination.after - Get messages created after this timestamp
 * @returns Messages for the chat, optionally paginated
 */
export const getMessagesByChatId = async (
  chatId: string,
  limit?: number,
  pagination?: {
    before?: string | null
    after?: string | null
  },
  client?: SupabaseClient<Database>
) => {
  if (!client && typeof window !== 'undefined') {
    return readBrowserMessagesByChatId(chatId, limit, pagination)
  }

  const c = getDefaultClient(client)
  let query = c
    .from('messages')
    .select('*, file_items (*)')
    .eq('chat_id', chatId)
    .order('sequence_number', { ascending: false })
    .order('created_at', { ascending: false })

  // Apply pagination if provided
  if (pagination) {
    if (pagination.before) {
      query = query.lt('created_at', pagination.before)
    }
    if (pagination.after) {
      query = query.gt('created_at', pagination.after)
    }
  }

  // Apply limit if provided
  if (limit && typeof limit === 'number' && limit > 0) {
    query = query.limit(limit)
  }

  const { data: messages, error } = await query

  if (error) {
    isDev && console.error('Error fetching messages:', error)
    throw new Error('Messages not found')
  }

  // Return messages in chronological order (oldest first) for display
  return messages?.reverse() || []
}

type CreateMessageOptions = {
  skipEmbeddings?: boolean
}

export const createMessage = async (
  message: TablesInsert<'messages'>,
  client?: SupabaseClient<Database>,
  options: CreateMessageOptions = {}
) => {
  if (!client && typeof window !== 'undefined') {
    const [createdMessage] = await createBrowserMessages([message])
    return createdMessage
  }

  const c = getDefaultClient(client)
  const { data: createdMessage, error } = await c
    .from('messages')
    .insert([message])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  // Add embeddings to the message content for semantic search
  // Only process messages with substantial content (ignore system messages, etc.)
  if (
    !options.skipEmbeddings &&
    createdMessage &&
    createdMessage.content &&
    createdMessage.content.length > 10
  ) {
    // Use a non-blocking call to add embeddings in the background
    addEmbeddingsToMessage(createdMessage.id, createdMessage.content, createdMessage.user_id).catch(
      (err) => {
        isDev && console.error('Failed to add embeddings to message:', err)
      }
    )
  }

  return createdMessage
}

export const createMessages = async (
  messages: TablesInsert<'messages'>[],
  client?: SupabaseClient<Database>,
  options: CreateMessageOptions = {}
): Promise<Tables<'messages'>[]> => {
  if (messages.length === 0) {
    return []
  }

  if (!client && typeof window !== 'undefined') {
    return createBrowserMessages(messages)
  }

  const c = getDefaultClient(client)
  const { data: createdMessages, error } = await c
    .from('messages')
    .insert(messages)
    .select('*')

  if (error) {
    throw new Error(error.message)
  }

  const savedMessages = (createdMessages ?? []) as Tables<'messages'>[]

  if (savedMessages.length !== messages.length) {
    throw new Error(
      `Expected ${messages.length} messages to persist, but saved ${savedMessages.length}`
    )
  }

  // Add embeddings to messages for semantic search
  // Process in background to not block the message creation flow
  if (!options.skipEmbeddings && savedMessages.length > 0) {
    // Process each message asynchronously
    Promise.all(
      savedMessages
        .filter((msg) => msg.content && msg.content.length > 10 && msg.role !== 'system')
        .map((msg) =>
          addEmbeddingsToMessage(msg.id, msg.content, msg.user_id).catch((err) => {
            isDev && console.error(`Failed to add embeddings to message ${msg.id}:`, err)
            return false
          })
        )
    ).catch((err) => {
      isDev && console.error('Error processing message embeddings:', err)
    })
  }

  return savedMessages
}

export const updateMessage = async (
  messageId: string,
  message: Partial<TablesUpdate<'messages'>>,
  client?: SupabaseClient<Database>
): Promise<Tables<'messages'>> => {
  if (!client && typeof window !== 'undefined') {
    return updateBrowserMessage(messageId, message)
  }

  const c = getDefaultClient(client)
  const { data, error } = await c
    .from('messages')
    .update(message)
    .eq('id', messageId)
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  const updatedMessage = data as Tables<'messages'>
  const updatedContent = typeof message.content === 'string' ? message.content : null

  if (updatedContent && updatedContent.length > 10 && updatedMessage.role !== 'system') {
    addEmbeddingsToMessage(
      updatedMessage.id,
      updatedContent,
      updatedMessage.user_id
    ).catch((err) => {
      isDev && console.error(`Failed to update embeddings for message ${messageId}:`, err)
    })
  }

  return updatedMessage
}

export const updateMessageFeedback = async (
  sessionId: string,
  modelId: string,
  providerId: string,
  messageId: string,
  thumbsUp: boolean | null,
  client?: SupabaseClient<Database>
) => {
  const feedbackUpdate: Partial<TablesUpdate<'messages'>> = {
    thumbs_up: thumbsUp,
    session_id: sessionId,
    model_id: modelId,
    provider_id: providerId
  }

  if (!client && typeof window !== 'undefined') {
    const data = await updateBrowserMessage(messageId, feedbackUpdate)

    try {
      await submitFeedback(sessionId, thumbsUp === true ? 1 : thumbsUp === false ? 0 : 0, {
        model: modelId,
        provider: providerId
      })
    } catch (feedbackError) {
      isDev && console.error('Failed to submit feedback:', feedbackError)
    }

    return data
  }

  const c = getDefaultClient(client)
  const { data, error } = await c
    .from('messages')
    .update(feedbackUpdate)
    .eq('id', messageId)
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  // Call submitFeedback after updating the message
  if (data) {
    try {
      await submitFeedback(sessionId, thumbsUp === true ? 1 : thumbsUp === false ? 0 : 0, {
        model: modelId,
        provider: providerId
      })
    } catch (feedbackError) {
      isDev && console.error('Failed to submit feedback:', feedbackError)
      // Optionally, you can choose to throw this error or handle it differently
    }
  }

  return data
}

export const updateMessageFeedbackForUser = async (
  userId: string,
  sessionId: string,
  modelId: string,
  providerId: string,
  messageId: string,
  thumbsUp: boolean | null,
  client?: SupabaseClient<Database>
) => {
  const message = await getMessageByIdForUser(messageId, userId, client)

  if (!message) {
    throw new Error('Message not found')
  }

  return updateMessageFeedback(
    sessionId,
    modelId,
    providerId,
    messageId,
    thumbsUp,
    client
  )
}

export const deleteMessage = async (
  messageId: string,
  client?: SupabaseClient<Database>
) => {
  const c = getDefaultClient(client)
  const { error } = await c.from('messages').delete().eq('id', messageId)

  if (error) {
    throw new Error(error.message)
  }

  return true
}

export async function deleteMessagesIncludingAndAfter(
  userId: string,
  chatId: string,
  sequenceNumber: number,
  client?: SupabaseClient<Database>
) {
  const c = getDefaultClient(client)
  const { error } = await c.rpc('delete_messages_including_and_after', {
    p_user_id: userId,
    p_chat_id: chatId,
    p_sequence_number: sequenceNumber
  })

  if (error) {
    return {
      error: 'Failed to delete messages.'
    }
  }

  return true
}

// New functions from the second codebase
// export const deleteMessagesIncludingAndAfterTurn = async (
//   chatId: string,
//   turn: number
// ) => {
//   const { error } = await supabase.rpc("delete_messages_including_and_after_turn", {
//     p_chat_id: chatId,
//     p_turn: turn
//   })

//   if (error) {
//     throw new Error("Failed to delete messages including and after the turn.")
//   }

//   return true
// }

// export const deleteRegeneratedMessages = async (
//   chatId: string,
//   turn: number
// ) => {
//   const { error } = await supabase.rpc("delete_regenerated_messages", {
//     p_chat_id: chatId,
//     p_turn: turn
//   })

//   if (error) {
//     throw new Error("Failed to delete regenerated messages.")
//   }

//   return true
// }

// export const getMessagesByChatIdAndTurn = async (chatId: string, turn: number) => {
//   const { data: messages, error } = await supabase
//     .from("messages")
//     .select("*")
//     .eq("chat_id", chatId)
//     .eq("turn", turn)

//   if (error) {
//     throw new Error("Failed to get messages by chat ID and turn.")
//   }

//   if (!messages) {
//     throw new Error("Messages not found")
//   }

//   return messages
// }

// export const updateTurnPreferences = async (
//   chatId: string,
//   turn: number,
//   preferences: { id: string, isPreferred: boolean }[]
// ) => {
//   const { error } = await supabase.rpc("update_turn_preferences", {
//     p_chat_id: chatId,
//     p_turn: turn,
//     p_preferences: preferences
//   })

//   if (error) {
//     throw new Error("Failed to update turn preferences.")
//   }

//   return true
// }

// export const submitFeedback = async (
//   sessionId: string | null,
//   rating: number,
//   feedback: { provider: string, model: string }
// ) => {
//   const { error } = await supabase.rpc("submit_feedback", {
//     p_session_id: sessionId,
//     p_rating: rating,
//     p_feedback: feedback
//   })

//   if (error) {
//     throw new Error("Failed to submit feedback.")
//   }

//   return true
// }

// export const handleBothResponsesAreBad = async ({
//   messages,
//   sessionId
// }: {
//   messages: { id: string, chatId: string, turn: number, provider: string, model: string }[],
//   sessionId: string | null
// }) => {
//   const promises = messages.map(async message => {
//     if (sessionId) {
//       await submitFeedback(sessionId, -1, {
//         provider: message.provider,
//         model: message.model
//       })
//     }

//     await updateMessage(message.id, {
//       thumbsUp: false
//     })

//     await updateTurnPreferences(
//       message.chatId,
//       message.turn,
//       messages.map(msg => ({ id: msg.id, isPreferred: false }))
//     )
//   })

//   await Promise.all(promises)
// }

// export const handleBothResponsesAreGood = async ({
//   messages
// }: {
//   messages: { id: string, chatId: string, turn: number }[]
// }) => {
//   const promises = messages.map(async message => {
//     await updateMessage(message.id, {
//       thumbsUp: false
//     })

//     await updateTurnPreferences(
//       message.chatId,
//       message.turn,
//       messages.map(msg => ({ id: msg.id, isPreferred: true }))
//     )
//   })

//   await Promise.all(promises)
// }

/* // Removing faulty function
export const getImageCountForModel = async (
  userId: string,
  date = new Date()
) => {
  const { data, error } = await supabase
    .from("daily_message_count" as any)
    .select("count")
    .eq("user_id", userId)
    .eq("type", "image") // <-- Problematic filter
    .gte("created_at", date.toISOString().split("T")[0]) // <-- Problematic filter
    .single()

  if (error) {
    isDev && console.error("Error fetching image count:", error)
    return 0
  }

  return data?.count || 0
}
*/

export const getDailyMessageCount = async (
  userId: string,
  modelId: string,
  client?: SupabaseClient<Database>
) => {
  try {
    const c = getDefaultClient(client)
    const { data, error } = await c
      .from('daily_message_count' as any)
      .select('*')
      .eq('user_id', userId)
      .eq('model_id', modelId)
      .eq('day', new Date().toISOString().split('T')[0])
      .single()

    if (error) {
      isDev && console.error('Error fetching daily message count:', error)
      return 0
    }

    return data?.count || 0
  } catch (error) {
    isDev && console.error('Error in getDailyMessageCount:', error)
    return 0
  }
}

export const incrementDailyMessageCount = async (
  userId: string,
  modelId: string,
  client?: SupabaseClient<Database>
) => {
  try {
    const c = getDefaultClient(client)
    const { error } = await c.from('daily_message_count' as any).upsert(
      {
        user_id: userId,
        model_id: modelId,
        day: new Date().toISOString().split('T')[0],
        count: 1
      },
      {
        onConflict: 'user_id,model_id,day',
        ignoreDuplicates: false
      }
    )

    if (error) {
      isDev && console.error('Error incrementing daily message count:', error)
    }
  } catch (error) {
    isDev && console.error('Error in incrementDailyMessageCount:', error)
  }
}

export async function deleteMessagesBySequenceNumber(
  chatId: string,
  sequenceNumber: number,
  options?: { client?: SupabaseClient<Database>; userId?: string }
) {
  const c = getDefaultClient(options?.client)
  let userId = options?.userId
  if (!userId && typeof window !== 'undefined') {
    const { data } = await require('@/lib/supabase/browser-client').supabase.auth.getUser()
    userId = data.user?.id ?? undefined
  }
  if (!userId) {
    throw new Error('User ID is required for deleteMessagesBySequenceNumber')
  }
  const { error } = await c.rpc('delete_messages_including_and_after', {
    p_chat_id: chatId,
    p_sequence_number: sequenceNumber,
    p_user_id: userId
  })

  if (error) {
    throw new Error(error.message)
  }
}

export async function getSharedMessage(
  messageId: string,
  client?: SupabaseClient<Database>
) {
  const c = getDefaultClient(client)
  const { data: message, error } = await c
    .from('messages')
    .select('*')
    .eq('id', messageId)
    .eq('sharing', 'public')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return message
}

// --------------

/**
 * Toggle the starred state of a message.
 * Starring is independent of feedback rating (thumbs_up).
 */
export const updateMessageStarred = async (
  messageId: string,
  starred: boolean,
  client?: SupabaseClient<Database>
) => {
  if (!client && typeof window !== 'undefined') {
    return updateBrowserMessage(messageId, { starred } as any)
  }

  const c = getDefaultClient(client)
  const { data, error } = await c
    .from('messages')
    .update({ starred })
    .eq('id', messageId)
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

// --------------

/**
 * Toggle starred state for a message owned by a specific user.
 */
export const updateMessageStarredForUser = async (
  messageId: string,
  userId: string,
  starred: boolean,
  client?: SupabaseClient<Database>
) => {
  const message = await getMessageByIdForUser(messageId, userId, client)

  if (!message) {
    throw new Error('Message not found')
  }

  return updateMessageStarred(messageId, starred, client)
}

// --------------

/**
 * Get starred messages for a user, ordered by most recent.
 */
export const getStarredMessagesForUser = async (
  userId: string,
  limit = 20,
  client?: SupabaseClient<Database>
) => {
  const c = getDefaultClient(client)
  const { data, error } = await c
    .from('messages')
    .select('id, chat_id, content, created_at, role, starred')
    .eq('user_id', userId)
    .eq('starred', true)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    throw new Error(error.message)
  }

  return data ?? []
}
